import BroadcastChannel from "@/utils/broadcasts";
import { Readable, derived, get, writable } from "svelte/store";

import { CartTransaction, Coupon, Fee, Permit, PermitOptions, Rebate, Snapshot, SnapshotItem, UnseatedTicket } from "@ticketmachine/sdk";

import Spinner from "tm:widgets/spinner";

import client from "@/utils/http";
import { Queue } from "@/utils/promises";
import { uuid4 } from "@/utils/crypto";
import { language } from "@/utils/translations";

import { URLS } from "./urls";
import { LockBackend } from "@/utils/locks";

interface SharedSnapshot {
  generation: string;
  snapshot: CartState
}

interface CartItem {
    type: string,
    id: number,
}

interface _Permit extends CartItem {
    discountRun: string|null,
    event: { evDate: string, evName: string, id: number, eventTags: {name: string}[]},
    category: { ecName: string, id: number},
    discount: { ecdName: string, id: number }|null,
}

interface CartPermit extends _Permit {
    type: "Permit"
}

interface CartSeatedPermit extends _Permit {
    type: "SeatedPermit",
    seat: {
        id: number,
        blockName: string,
        rowName: string,
        seatName: string
    }
}

interface CartGiftCoupon extends CartItem {
    type: "GiftCoupon",
    amount: number
}

interface CartRebateCoupon extends CartItem {
    type: "RebateCoupon",
    annotation: string
}

interface CartStaticSellable extends CartItem {
    type: "StaticSellable",
    discountRun: string|null,
    itemType: string
}

interface PriceModification {
    type: string,
    internalType: string,
    description: string,
    internal: boolean
}

interface AdditiveModification extends PriceModification {
    type: "AdditiveModification",
    offset: number
}

interface MultiplicativeModification extends PriceModification {
    type: "MultiplicativeModification",
    numerator: number,
    denominator: number
}

interface SetPriceModification extends PriceModification {
    type: "SetPriceModification",
    newPrice: number
}

type CartItemType = CartSeatedPermit|CartPermit|CartGiftCoupon|CartRebateCoupon|CartStaticSellable|Fee;

export interface RawSnapshot {
    basePrice: number,
    name: string,
    item: CartItemType,
    priceModifications: (AdditiveModification|MultiplicativeModification|SetPriceModification)[]
}


export interface CartState {
    snapshot: RawSnapshot[],
    expiry: string|null
}

export function readCart<T>(cb: () => Promise<T>): Promise<T> {
    return LockBackend.shared("carts", cb);
}
export function writeCart<T>(cb: () => Promise<T>): Promise<T> {
    return LockBackend.exclusive("carts", cb);
}

function getSum(item: RawSnapshot): number {
  let price = item.basePrice;
  for (let modification of item.priceModifications) {
    if (modification.internal)
      continue;

    switch (modification.type) {
      case "AdditiveModification":
        price += modification.offset;
        break;

      case "MultiplicativeModification":
        const mul = modification as MultiplicativeModification;
        price = Math.round((price * mul.numerator / mul.denominator))
        break;

      case "SetPriceModification":
        price = (modification as SetPriceModification).newPrice;
        break;
    }
  }
  return price;
}

function getVat(item: RawSnapshot): number {
  let sum = 0;
  
  let price = item.basePrice;
  for (let modification of item.priceModifications) {
    if (modification.internal) {
      if (modification.internalType !== "vat") continue;

      switch (modification.type) {
        case "MultiplicativeModification":
          const mul = modification as MultiplicativeModification;
          sum += Math.round((price * mul.numerator / mul.denominator))
          break;
        case "SetPriceModification":
          sum += (modification as SetPriceModification).newPrice;
          break;
        default:
          throw new Error("Unexpected VAT modification type.");
      }
    }

    switch (modification.type) {
      case "AdditiveModification":
        price += modification.offset;
        break;

      case "MultiplicativeModification":
        const mul = modification as MultiplicativeModification;
        price = Math.round((price * mul.numerator / mul.denominator))
        break;

      case "SetPriceModification":
        price = (modification as SetPriceModification).newPrice;
        break;
    }
  }
  return sum;
}

function getDiscounts(item: RawSnapshot): string[] {
  return item.priceModifications.filter(s => !s.internal).map(s => s.description);
}

export function readSnapshot(s: RawSnapshot[]): SnapshotItem[] {
  return s.map(item => {
    switch (item.item.type) {
      case "Permit":
        return {
          type: "permit",
          id: item.item.id,
          price: getSum(item),
          vat_price: getVat(item),

          event_id: item.item.event.id,
          event_name: item.item.event.evName,
          event_date: new Date(item.item.event.evDate),

          category_id: item.item.category.id,
          category_name: item.item.category.ecName,

          base_discount_id: item.item.discount?.id,
          base_discount_name: item.item.discount?.ecdName,

          discounts: getDiscounts(item),
          bundle_run: item.item.discountRun,

          seat: null,
          event_tags: item.item.event.eventTags.map(t => t.name)
        } as Permit;

      case "SeatedPermit":
        return {
          type: "permit",
          id: item.item.id,
          price: getSum(item),
          vat_price: getVat(item),

          event_id: item.item.event.id,
          event_name: item.item.event.evName,
          event_date: new Date(item.item.event.evDate),

          category_id: item.item.category.id,
          category_name: item.item.category.ecName,

          base_discount_id: item.item.discount?.id,
          base_discount_name: item.item.discount?.ecdName,

          discounts: getDiscounts(item),
          bundle_run: item.item.discountRun,

          seat: {
            id: "" + item.item.seat.id,
            seat_name: item.item.seat.seatName,
            row_name: item.item.seat.rowName,
            block_name: item.item.seat.blockName
          },
          event_tags: item.item.event.eventTags.map(t => t.name)
        } as Permit;

      case "GiftCoupon":
        return {
          type: "coupon",
          id: item.item.id,
          price: getSum(item),
          vat_price: getVat(item)
        } as Coupon;

      case "RebateCoupon":
        const annotation = JSON.parse(item.item.annotation);
        return {
          type: "rebate",
          name: annotation.name_i18n[language] || annotation.name,
          id: item.item.id,
          price: getSum(item),
          vat_price: getVat(item)
        } as Rebate;

      case "StaticSellable":
        return {
          type: "fee",
          bundle_run: null,
          description: item.name,
          fee_type: item.item.itemType || "fee",
          id: item.item.id,
          price: getSum(item),
          vat_price: getVat(item)
        } as Fee

      default:
        return {
          type: "fee",
          bundle_run: null,
          description: item.name,
          fee_type: item.item.fee_type,
          id: item.item.id,
          price: getSum(item),
          vat_price: getVat(item)
        } as Fee
    }
  });

}


async function getRawSnapshot(urls: URLS): Promise<CartState> {
    return await readCart(async () => (await client.get<CartState>(urls.CurrentCartURL)).data);
}


export async function getSnapshot(urls: URLS): Promise<Snapshot> {
  return buildSnapshot(await getRawSnapshot(urls));

}

function buildSnapshot({expiry, snapshot}: CartState): Snapshot {
  const converted = readSnapshot(snapshot);
  
  return {
    expiry_time: expiry !== null ? new Date(expiry) : null,
    items: converted
  }
}

export interface BlockedSeat {
  id: number,
  sellable_id: number,
  category_id: number

  seat_name: string,
  row_name: string,
  block_name: string,

}


export const editQueue = new Queue();


export async function createSnapshotListener(name: string, urls: URLS): Promise<[Readable<Snapshot>, () => Promise<void>]> {
  let currentGeneration: string = uuid4();
  const currentSnapshot = writable<SharedSnapshot>({
    generation: currentGeneration,
    snapshot: await getRawSnapshot(urls)
  });

  const channel = new BroadcastChannel(name);
  channel.addEventListener("message", (msg: MessageEvent<SharedSnapshot>) => {
    const s = msg.data;
    if (currentGeneration === s.generation) return;
    currentGeneration = s.generation;
    currentSnapshot.set(s);
  });
  currentSnapshot.subscribe((s) => {
    if (currentGeneration === s.generation) return;
    currentGeneration = s.generation;
    channel.postMessage({event: "changed", generation: s.generation, snapshot: s.snapshot});
  });
  
  return [
    derived(currentSnapshot, ({snapshot}) => buildSnapshot(snapshot)),
    async () => {
      const nextId = uuid4();
      const pSnapshot = getRawSnapshot(urls)
      pSnapshot.catch(window.reportError||console.error);
      const snapshot = await pSnapshot;
      currentSnapshot.set({generation: nextId, snapshot});
    }
  ]
}



const MAX_TRX_SIZE = 100;


export function createCartConnector(snapshot: Readable<Snapshot>, updateCart: () => Promise<void>, urls: URLS) {
  async function sellableIdFromSeatId(seat_id: number): Promise<number|null> {
      const snap = get(snapshot);
      const inCart = (snap.items.find((s: any) => s.type === "permit" && (s as Permit).seat?.id === seat_id.toString()));
      if (!!inCart) { return inCart.id; }

      return null;
  }

  async function buildBatch(block: (trx: CartTransaction) => Promise<void>): Promise<[string, any][]> {
      const queue: [string, any][] = [];
      const trx: CartTransaction = {
        async ensureReloadCart(): Promise<void> {},

        async addUnseatedPermit(permit: UnseatedTicket, count: number, options: PermitOptions) {
          queue.push(["addUnseated", {
            count,
            event_id: permit.event,
            category_id: permit.category,
            discount_id: permit.discount,
            form: options["form"] || undefined,
            coupon_code: options["coupon"] || undefined
          }]);
        },

        async removeSellable(sellable_id: number) {
          queue.push(["expire", {sellable_id}]);
        },

        async updateSellable(sellable_id: number, type: UnseatedTicket|null, options: PermitOptions|null) {
          if (type !== null) {
            const discount_id = type.discount || null;
            queue.push(["setDiscount", {sellable_id, discount_id: discount_id}])
          }
          if (options !== null)  {
            queue.push(["updateForm", {
              sellable_id,
              form: options["form"] || null
            }])
          }
        },

        /**
         * Blocks a seat
         */
        async blockSeat(event_id: number, seat_id: number, discount_id: number|null): Promise<void> {
          queue.push(["blockSeat", {event_id, seat_id, with_discount: true, discount_id}]);
        },

        /**
         * Unblocks a seat
         */
        async unblockSeat(event_id: number, seat_id: number): Promise<void> {
          const id = await sellableIdFromSeatId(seat_id);
          if (id !== null) queue.push(["expire", {sellable_id: id}]);
        },

        
      } as any as CartTransaction;

      await block(trx);
      return queue;
  }

  return {
    whenCartUpdated() { return snapshot },

    /**
     * Checks the validity of a coupon code.
     */
    async checkCouponCode(code: string): Promise<{success: boolean, reason: string}> {
        return await editQueue.enqueue(async () => {
            return await readCart(async () => {
                return (await client.get(urls.CheckCouponCodeURL, {params: {code}})).data;
            });
        });
    },

    /**
     * Applies a given coupon code.
     */
    async applyCouponCode(code: string): Promise<{success: boolean, reason: string}> {
        return await editQueue.enqueue(async () => {
            const result = await writeCart(async () => await client.post(urls.ApplyCouponCodeURL, {code}));
            await updateCart();
            return result.data;
        });
    },

    /**
     * Applies a rebate
     */
    async applyBundle(key: string): Promise<{success: boolean, reason: string}> {
        return await editQueue.enqueue(async () => {
            const result = 
                await writeCart(async () => {
                    return (await client.post(urls.ApplyRebateURL, {idx: key})).data;
                });
            await updateCart();
            return result;
        });
    },
    
    /**
     * Checks a rebate
     */
    async checkBundle(key: string): Promise<{success: boolean, reason: string}> {
        return await editQueue.enqueue(async () => {
            return await readCart(async () => {
                return (await client.get(urls.CheckRebateURL, {params: {idx: key}})).data;
            });
        });
    },

    /**
     * Adds one or more gift-cards to the cart.
     */
    async buyGiftCard(amount: number, count: number, options: any = {}) {
        return await editQueue.enqueue(async () => {
            await writeCart(async () => await client.post(urls.AddGiftCoupon, {amount: amount*100, count, options}));
            await updateCart();
        });
    },

    /**
     * Not implemented.
     */
    mountCheckout() { return null as (() => void)|null; },

    /**
     * Adds one or more rebate-cards to the cart.
     */
    async buyRebateCard(key: string, count: number, options: any = {}) {
        return await editQueue.enqueue(async () => {
            await writeCart(async () => await client.post(urls.AddRebateCoupon, {idx: key, count, options}));
            await updateCart();
        });
    },

    async showForms() {
        const forms = (await readCart(async () => await client.get(urls.ShowFormsURL))).data;
        const result: Map<number, Record<string, string>> = new Map();
        Object.entries(forms).forEach(([k, v]) => result.set(Number(k), v as any));
        return result;
    },

    async runTransaction(block: (trx: CartTransaction) => Promise<void>): Promise<void> {
      const queue = await buildBatch(block);
      await editQueue.enqueue(async () => {
        await Spinner.run(async () => {
          let opList: [string, any][] = [];
          await writeCart(async () => {
              while (queue.length > 0) {
                  opList.push(queue.shift()!);
                  if (opList.length >= MAX_TRX_SIZE) {
                      await client.post(urls.CartBatchOperationURL, {operations: opList.map(([op, args]) => ({op, args}))});
                      opList = [];
                  }
              }
              if (opList.length > 0)
                  await client.post(urls.CartBatchOperationURL, {operations: opList.map(([op, args]) => ({op, args}))});
          });

          await updateCart();
        });
      });
    },

    async tryTransaction(block: (trx: CartTransaction) => Promise<void>): Promise<Snapshot> {
      const queue = await buildBatch(block);
      // While this technically doesn't have side-effects,
      // internally, it performs a database transactions, so to mitigate possible deadlocks,
      // we're still hold the lock exclusively.
      const raw = await writeCart(async () => {
        return await client.post<CartState>(urls.CartTryBatchOperationURL, {operations: queue.map(([op, args]) => ({op, args}))});
      });
      const converted = readSnapshot(raw.data.snapshot);
      const expiry = raw.data.expiry;

      return {
          expiry_time: expiry !== null ? new Date(expiry) : null,
          items: converted
      };
    }
  }
}
